import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBeautyProduct } from '../../../beauty-shared/context/BeautyProductContext';
import {
  StudioLookPickerSubTabs,
  StudioOptionsFullViewModal,
  StudioOptionsFullViewTrigger,
} from '../../../beauty-shared/components/StudioOptionsFullViewModal';
import '../../../beauty-shared/styles/StudioOptionsFullViewModal.css';
import LooksActionsRail from '../../looks/components/LooksActionsRail';
import LooksCompareBoard from '../../looks/components/LooksCompareBoard';
import { createLooksDownloadUrl } from '../../looks/utils/looksApi';
import '../../looks/pages/LooksStudio.css';
import StudioPhotoSourceModal from '../../makeuphair/components/StudioPhotoSourceModal';
import MakeupAiLoader from '../../makeuphair/components/MakeupAiLoader';
import '../../makeuphair/components/MakeupAiLoader.css';
import {
  APPLY_LOADER_DONE,
  APPLY_LOADER_TITLE,
  RECOMMEND_LOADER_DONE,
  RECOMMEND_LOADER_TITLE,
  applyMessageForProgress,
  finishLoaderUi,
  recommendMessageForProgress,
  setApplyLoaderStep,
} from '../../makeuphair/components/makeupLoaderCopy';
import { SHELL_MQ, matchesShellViewport } from '../../shared/tabletMedia';
import { renderGuestQrDataUrl } from '../../shared/guestQrCode';
import {
  beginFreshLookCapture,
  LOOK_MODULE_IDS,
  resolveStudioEntryImage,
  saveLookResult,
} from '../../shared/lookSession';
import {
  COMPARE_ORIGINAL_ID,
  appendLookToHistory,
  handleCompareLookSelect,
  primeCompareSlotsForNewLook,
  resolveStudioDownloadImage,
} from '../../shared/compareLookHistory';
import MakeupV3BridalLooksGrid from '../../makeup-flux-prompt-v3/components/MakeupV3BridalLooksGrid';
import MakeupV3ColorEditor from '../../makeup-flux-prompt-v3/components/MakeupV3ColorEditor';
import MakeupV3EverydayPresetsRail from '../../makeup-flux-prompt-v3/components/MakeupV3EverydayPresetsRail';
import MakeupV3MenuTabs from '../../makeup-flux-prompt-v3/components/MakeupV3MenuTabs';
import { bridalLookById } from '../../makeup-flux-prompt-v3/utils/makeupV3BridalLooks';
import { useMakeupV3StudioState } from '../../makeup-flux-prompt-v3/hooks/useMakeupV3StudioState';
import { MAKEUP_V3_BRIDAL_LOOK_PRESET_ID } from '../../makeup-flux-prompt-v3/utils/makeupV3BridalLooks';
import {
  MAKEUP_V3_LOOK_PICKER_TABS,
  filterBridalLooks,
  filterEverydayPresets,
} from '../../makeup-flux-prompt-v3/utils/makeupV3LookPickerTabs';
import '../../makeup-flux-prompt-v3/pages/MakeupFluxPromptV3Page.css';


const LOOKS_KIOSK_LAYOUT_MQ = [
  SHELL_MQ,
  '(orientation: portrait) and (max-width: 1366px)',
  '(orientation: landscape) and (max-width: 1400px) and (max-height: 920px)',
].join(', ');

function matchesLooksKioskLayout(win = typeof window !== 'undefined' ? window : null) {
  if (!win?.matchMedia) return matchesShellViewport(win);
  return win.matchMedia(LOOKS_KIOSK_LAYOUT_MQ).matches;
}

/** Makeup v3 only — preview left, options right; does not change LooksStudio tab MQs. */
const MAKEUP_V3_SIDE_BY_SIDE_MQ = [
  '(min-width: 1025px) and (pointer: fine)',
  '(orientation: landscape) and (min-width: 960px) and (max-width: 1600px) and (max-height: 920px)',
].join(', ');

function matchesMakeupV3SideBySide(win = typeof window !== 'undefined' ? window : null) {
  if (!win?.matchMedia) return false;
  return win.matchMedia(MAKEUP_V3_SIDE_BY_SIDE_MQ).matches;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.readAsDataURL(file);
  });
}

export default function MakeupV3KioskStudio() {
  const location = useLocation();
  const navigate = useNavigate();
  const { title: pageTitle, naturalsAppBase } = useBeautyProduct();
  const capturePath = `${naturalsAppBase || '/naturals/makeup-studio-test'}/capture`;
  const fileInputRef = useRef(null);
  const panelDragRef = useRef({ startY: 0, dragging: false, moved: false });
  const prevApplyBusyRef = useRef(false);
  const prevAnalyzeBusyRef = useRef(false);
  const qrTitleId = useId();

  const [isKioskViewport, setIsKioskViewport] = useState(() => matchesLooksKioskLayout());
  const [isV3SideBySide, setIsV3SideBySide] = useState(() => matchesMakeupV3SideBySide());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [menuTab, setMenuTab] = useState('everyday');
  const [everydayLookTab, setEverydayLookTab] = useState('recommended');
  const [bridalLookTab, setBridalLookTab] = useState('all');
  const [makeupOptionsFullView, setMakeupOptionsFullView] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [appliedLooks, setAppliedLooks] = useState([]);
  const [compareLeftId, setCompareLeftId] = useState(COMPARE_ORIGINAL_ID);
  const [compareRightId, setCompareRightId] = useState(null);
  const [comparePickSlot, setComparePickSlot] = useState('right');
  const [compareMode, setCompareMode] = useState('side');
  const [comparePosition, setComparePosition] = useState(50);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrBusy, setQrBusy] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrError, setQrError] = useState('');
  const [qrImageOverride, setQrImageOverride] = useState('');
  const [qrLabelOverride, setQrLabelOverride] = useState('');
  const [showPhotoSourceModal, setShowPhotoSourceModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loaderMessage, setLoaderMessage] = useState('');
  const [loaderTitle, setLoaderTitle] = useState('');
  const [loaderFinishing, setLoaderFinishing] = useState(false);

  const [sourceImage, setSourceImage] = useState(() => resolveStudioEntryImage(location.state));

  useEffect(() => {
    const mqKiosk = window.matchMedia(LOOKS_KIOSK_LAYOUT_MQ);
    const mqSide = window.matchMedia(MAKEUP_V3_SIDE_BY_SIDE_MQ);
    const sync = () => {
      setIsKioskViewport(matchesLooksKioskLayout());
      setIsV3SideBySide(matchesMakeupV3SideBySide());
    };
    sync();
    mqKiosk.addEventListener?.('change', sync);
    mqSide.addEventListener?.('change', sync);
    return () => {
      mqKiosk.removeEventListener?.('change', sync);
      mqSide.removeEventListener?.('change', sync);
    };
  }, []);

  useEffect(() => {
    const img = resolveStudioEntryImage(location.state);
    if (img) {
      setSourceImage(img);
      return;
    }
    navigate(capturePath, { replace: true });
  }, [location.state, navigate, capturePath]);

  const onResult = useCallback(async (imageUrl, historyMeta = {}) => {
    if (!imageUrl) return;
    const label = historyMeta.label || 'Makeup look';
    let newId = '';
    setAppliedLooks((prev) => {
      const next = appendLookToHistory(prev, {
        image: imageUrl,
        label,
        idPrefix: 'look',
        extra: {
          detailTitle: historyMeta.detailTitle || label,
          detailLines: historyMeta.detailLines || [],
        },
      });
      newId = next[0]?.id || '';
      return next;
    });
    if (newId) {
      primeCompareSlotsForNewLook(newId, {
        setCompareLeftId,
        setCompareRightId,
        setComparePickSlot,
      });
    }
    await saveLookResult(LOOK_MODULE_IDS.MAKEUP, imageUrl);
  }, []);

  const studio = useMakeupV3StudioState({
    initialSourceImage: sourceImage,
    onResult,
  });

  const customCategories = useMemo(
    () => studio.categories.filter((c) => c !== 'contour' && c !== 'eyebrows'),
    [studio.categories],
  );

  const isKioskLayout = isKioskViewport && !isFullscreen;
  const useV3SideBySide = !isFullscreen && isV3SideBySide;

  const onMenuTabChange = useCallback(
    (nextTab) => {
      setMenuTab(nextTab);
      if (nextTab === 'bridal') studio.onMakeupPresetModeChange('bridal');
      if (nextTab === 'everyday') studio.onMakeupPresetModeChange('everyday');
      if (isKioskLayout) setPanelOpen(true);
      setMakeupOptionsFullView(false);
    },
    [studio, isKioskLayout],
  );

  const toggleMakeupOptionsFullView = useCallback(() => {
    setMakeupOptionsFullView((open) => !open);
  }, []);

  const everydayPresetsFiltered = useMemo(
    () => filterEverydayPresets(everydayLookTab),
    [everydayLookTab],
  );
  const bridalLooksFiltered = useMemo(() => filterBridalLooks(bridalLookTab), [bridalLookTab]);

  const makeupFullViewTitle = useMemo(() => {
    if (menuTab === 'bridal') {
      const sub = MAKEUP_V3_LOOK_PICKER_TABS.find((t) => t.id === bridalLookTab)?.label || '';
      return `Bridal · ${sub}`;
    }
    if (menuTab === 'everyday') {
      const sub = MAKEUP_V3_LOOK_PICKER_TABS.find((t) => t.id === everydayLookTab)?.label || '';
      return `Everyday · ${sub}`;
    }
    return 'Makeup options';
  }, [menuTab, bridalLookTab, everydayLookTab]);

  const onPickBridalLook = useCallback(
    (lookId) => {
      studio.applyBridalLook(lookId);
      setMenuTab('custom');
      setMakeupOptionsFullView(false);
      if (isKioskLayout) setPanelOpen(true);
    },
    [studio, isKioskLayout],
  );

  const onPickEverydayPreset = useCallback(
    (presetId) => {
      studio.applyPreset(presetId);
      if (presetId) {
        setMenuTab('custom');
        setMakeupOptionsFullView(false);
        if (isKioskLayout) setPanelOpen(true);
      }
    },
    [studio, isKioskLayout],
  );
  const displayImage = studio.resultImage || sourceImage;
  const canCompare = appliedLooks.length > 0;
  const analyzeLoaderActive = studio.skinBusy || studio.resolveBusy;
  const applyLoaderActive = studio.busy;
  const showMakeupLoader = analyzeLoaderActive || applyLoaderActive || loaderFinishing;

  useEffect(() => {
    if (!analyzeLoaderActive && !applyLoaderActive) return undefined;

    let progressTimer = null;
    const started = Date.now();

    if (applyLoaderActive) {
      setLoaderTitle(APPLY_LOADER_TITLE);
      setApplyLoaderStep(setLoaderProgress, setLoaderMessage, 8);
      progressTimer = window.setInterval(() => {
        setLoaderProgress((p) => {
          const next = Math.min(92, p + 1);
          setLoaderMessage(applyMessageForProgress(next));
          return next;
        });
      }, 650);
    } else {
      setLoaderTitle(RECOMMEND_LOADER_TITLE);
      setLoaderProgress(0);
      setLoaderMessage(recommendMessageForProgress(0));
      progressTimer = window.setInterval(() => {
        const elapsed = Date.now() - started;
        const eased = Math.min(92, Math.round(92 * (1 - Math.exp(-elapsed / 16000))));
        setLoaderProgress(eased);
        setLoaderMessage(
          studio.resolveBusy
            ? 'Matching preset colors to your skin tone…'
            : recommendMessageForProgress(eased),
        );
      }, 140);
    }

    return () => {
      if (progressTimer) window.clearInterval(progressTimer);
    };
  }, [analyzeLoaderActive, applyLoaderActive, studio.resolveBusy]);

  useEffect(() => {
    if (prevApplyBusyRef.current && !studio.busy) {
      setLoaderFinishing(true);
      (async () => {
        await finishLoaderUi(setLoaderProgress, setLoaderMessage, APPLY_LOADER_DONE);
        setLoaderFinishing(false);
        if (!studio.busy && !studio.skinBusy && !studio.resolveBusy) {
          setLoaderProgress(0);
          setLoaderMessage('');
          setLoaderTitle('');
        }
      })();
    }
    prevApplyBusyRef.current = studio.busy;
  }, [studio.busy, studio.skinBusy, studio.resolveBusy]);

  useEffect(() => {
    const analyzing = studio.skinBusy || studio.resolveBusy;
    if (prevAnalyzeBusyRef.current && !analyzing && !studio.busy && !loaderFinishing) {
      (async () => {
        await finishLoaderUi(setLoaderProgress, setLoaderMessage, RECOMMEND_LOADER_DONE);
        setLoaderProgress(0);
        setLoaderMessage('');
        setLoaderTitle('');
      })();
    }
    prevAnalyzeBusyRef.current = analyzing;
  }, [studio.skinBusy, studio.resolveBusy, studio.busy, loaderFinishing]);

  const applyIncomingFaceImage = useCallback(
    (dataUrl) => {
      if (!dataUrl) return;
      beginFreshLookCapture(dataUrl);
      setSourceImage(dataUrl);
      studio.resetResults();
      setAppliedLooks([]);
      setComparing(false);
      setCompareLeftId(COMPARE_ORIGINAL_ID);
      setCompareRightId(null);
      studio.setSourceImage(dataUrl);
      studio.runSkinAnalyze(dataUrl);
    },
    [studio],
  );

  const onToggleCompare = useCallback(() => {
    if (!canCompare) return;
    setComparing((prev) => {
      const next = !prev;
      if (next) {
        setCompareLeftId(COMPARE_ORIGINAL_ID);
        setCompareRightId((cur) => {
          if (cur && (cur === COMPARE_ORIGINAL_ID || appliedLooks.some((l) => l.id === cur))) {
            return cur;
          }
          return appliedLooks[0]?.id || null;
        });
        setComparePickSlot('right');
        setComparePosition(50);
      }
      return next;
    });
  }, [appliedLooks, canCompare]);

  const onSelectCompareLook = useCallback(
    (lookId) => {
      handleCompareLookSelect({
        lookId,
        comparePickSlot,
        compareLeftId,
        compareRightId,
        setCompareLeftId,
        setCompareRightId,
        setComparePickSlot,
      });
    },
    [comparePickSlot, compareLeftId, compareRightId],
  );

  const openDownloadQr = useCallback(
    (lookItem) => {
      if (lookItem?.image) {
        setQrImageOverride(lookItem.image);
        setQrLabelOverride(lookItem.label || 'Makeup look');
      } else {
        setQrImageOverride('');
        setQrLabelOverride('');
      }
      setShowQrModal(true);
    },
    [],
  );

  useEffect(() => {
    if (!showQrModal) return undefined;
    let cancelled = false;
    (async () => {
      const image =
        qrImageOverride ||
        resolveStudioDownloadImage({
          comparing,
          looks: appliedLooks,
          originalSrc: sourceImage,
          latestResultImage: studio.resultImage,
          sourceImage,
          comparePickSlot,
          compareLeftId,
          compareRightId,
        });
      if (!image) {
        setQrError('No look image to share');
        return;
      }
      setQrBusy(true);
      setQrError('');
      setQrDataUrl('');
      try {
        const url = await createLooksDownloadUrl(image, 'makeup-look');
        const dataUrl = await renderGuestQrDataUrl(url, 280);
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        if (!cancelled) setQrError(err.message || 'Could not prepare QR');
      } finally {
        if (!cancelled) setQrBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showQrModal,
    qrImageOverride,
    comparing,
    appliedLooks,
    studio.resultImage,
    sourceImage,
    comparePickSlot,
    compareLeftId,
    compareRightId,
  ]);

  useEffect(() => {
    if (!showQrModal) {
      setQrImageOverride('');
      setQrLabelOverride('');
    }
  }, [showQrModal]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('looks-fullscreen-active');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('looks-fullscreen-active');
    };
  }, [isFullscreen]);

  const onPanelHandlePointerDown = (event) => {
    if (!isKioskLayout) return;
    panelDragRef.current = { startY: event.clientY, dragging: true, moved: false };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPanelHandlePointerMove = (event) => {
    if (!panelDragRef.current.dragging) return;
    if (Math.abs(event.clientY - panelDragRef.current.startY) > 6) {
      panelDragRef.current.moved = true;
    }
  };

  const onPanelHandlePointerUp = (event) => {
    const { startY, dragging, moved } = panelDragRef.current;
    if (!dragging) return;
    panelDragRef.current.dragging = false;
    if (!isKioskLayout) return;
    const deltaY = event.clientY - startY;
    if (deltaY > 36) setPanelOpen(false);
    else if (deltaY < -36) setPanelOpen(true);
    else if (!moved) setPanelOpen((prev) => !prev);
  };

  const onResetConfirm = () => {
    studio.resetResults();
    setAppliedLooks([]);
    setComparing(false);
    setCompareLeftId(COMPARE_ORIGINAL_ID);
    setCompareRightId(null);
    setShowResetConfirm(false);
  };

  if (!sourceImage) return null;

  const qrModal =
    showQrModal && typeof document !== 'undefined'
      ? createPortal(
          <div className="looks-qr-modal" role="presentation" onClick={() => setShowQrModal(false)}>
            <div
              className="looks-qr-modal__card"
              role="dialog"
              aria-modal="true"
              aria-labelledby={qrTitleId}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="looks-qr-modal__close"
                aria-label="Close"
                onClick={() => setShowQrModal(false)}
              >
                ×
              </button>
              <h3 id={qrTitleId}>Scan to download</h3>
              <p>
                {qrLabelOverride
                  ? `Saving: ${qrLabelOverride}`
                  : comparing
                    ? 'Downloads the look on the active Left or Right slot — tap a slot label to switch.'
                    : 'Scan with your phone to save this makeup look.'}
              </p>
              <div className="looks-qr-modal__frame">
                {qrBusy ? (
                  <span>Preparing…</span>
                ) : qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR code to download look" />
                ) : (
                  <span>{qrError || 'QR unavailable'}</span>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  const resetModal =
    showResetConfirm && typeof document !== 'undefined'
      ? createPortal(
          <div className="hair-reset-overlay" role="dialog" aria-modal="true" aria-label="Reset look">
            <div className="hair-reset-modal">
              <h3>Reset look?</h3>
              <p>This clears applied makeup results. Your photo stays.</p>
              <div className="hair-reset-modal__actions">
                <button type="button" onClick={() => setShowResetConfirm(false)}>Cancel</button>
                <button type="button" className="is-danger" onClick={onResetConfirm}>Reset</button>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className={`looks-studio looks-studio--makeup-v3${isFullscreen ? ' is-fullscreen' : ''}${
        isKioskLayout ? ' looks-studio--kiosk' : ''
      }${isKioskLayout ? (panelOpen ? ' is-panel-open' : ' is-panel-collapsed') : ''}${
        isKioskLayout && menuTab === 'custom' ? ' looks-studio--custom-mode' : ''
      }${isKioskLayout && menuTab !== 'custom' ? ' looks-studio--browse' : ''}${
        useV3SideBySide ? ' looks-studio--v3-side-by-side' : ''
      }`}
    >
      {!isFullscreen ? (
        <header className="looks-studio__top">
          <h1 className="looks-studio__title">{pageTitle || 'Makeup Studio'}</h1>
        </header>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="looks-studio__file-input"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          try {
            applyIncomingFaceImage(await fileToDataUrl(file));
          } catch (err) {
            studio.setError(err.message || 'Could not upload image');
          }
        }}
      />

      <div className="looks-studio__stage">
        <div className={`looks-studio__preview-wrap${comparing ? ' is-comparing' : ''}`}>
          {comparing && sourceImage && appliedLooks.length > 0 ? (
            <LooksCompareBoard
              originalSrc={sourceImage}
              looks={appliedLooks}
              leftId={compareLeftId}
              rightId={compareRightId || appliedLooks[0]?.id || COMPARE_ORIGINAL_ID}
              pickSlot={comparePickSlot}
              onPickSlotChange={setComparePickSlot}
              onSelectLook={onSelectCompareLook}
              onDownloadLook={openDownloadQr}
              mode={compareMode}
              onModeChange={setCompareMode}
              sliderPosition={comparePosition}
              onSliderPositionChange={setComparePosition}
            />
          ) : displayImage ? (
            <img
              key={displayImage.slice(0, 48)}
              src={displayImage}
              alt="Makeup preview"
              className="looks-studio__preview"
            />
          ) : (
            <p className="looks-studio__empty">No photo yet</p>
          )}

          <LooksActionsRail
            canCompare={canCompare}
            canDownload={Boolean(studio.resultImage || sourceImage)}
            isComparing={comparing}
            isFullscreen={isFullscreen}
            onImport={() => setShowPhotoSourceModal(true)}
            onToggleCompare={onToggleCompare}
            onDownloadQr={() => (studio.resultImage || sourceImage) && openDownloadQr(null)}
            onToggleFullscreen={() => setIsFullscreen((v) => !v)}
            onRequestReset={() => setShowResetConfirm(true)}
          />

          {showMakeupLoader ? (
            <div className="looks-studio__overlay looks-studio__overlay--ai">
              <MakeupAiLoader
                active
                title={loaderTitle}
                progress={loaderProgress}
                message={loaderMessage}
              />
            </div>
          ) : null}
        </div>

        {panelOpen || !isKioskLayout ? (
          <div className="looks-studio__apply-bar">
            <button
              type="button"
              className="looks-btn looks-btn--primary looks-studio__apply-btn"
              disabled={!studio.labActive || studio.busy || studio.skinBusy || studio.resolveBusy}
              onClick={studio.onGenerate}
            >
              {studio.busy ? 'Applying…' : 'Apply makeup'}
            </button>
          </div>
        ) : null}

        {!isFullscreen ? (
          <div
            className={`looks-studio__panel${isKioskLayout ? ' looks-studio__panel--kiosk' : ''}${
              isKioskLayout ? (panelOpen ? ' is-open' : ' is-collapsed') : ''
            }`}
          >
            {isKioskLayout ? (
              <button
                type="button"
                className="looks-studio__panel-handle"
                aria-expanded={panelOpen}
                aria-label={panelOpen ? 'Collapse options panel' : 'Expand options panel'}
                onPointerDown={onPanelHandlePointerDown}
                onPointerMove={onPanelHandlePointerMove}
                onPointerUp={onPanelHandlePointerUp}
                onPointerCancel={onPanelHandlePointerUp}
              />
            ) : null}

            <MakeupV3MenuTabs
              activeTab={menuTab}
              disabled={studio.busy}
              onChange={onMenuTabChange}
            />

            <div className="looks-studio__panel-body makeup-v3-kiosk-panel">
              {studio.error ? <p className="looks-studio__error">{studio.error}</p> : null}

              {menuTab === 'everyday' ? (
                <>
                  {studio.skinBusy || studio.resolveBusy ? (
                    <p className="looks-studio__hint" role="status">
                      {studio.skinBusy ? 'Analyzing skin…' : 'Matching colors to your photo…'}
                    </p>
                  ) : null}
                  <StudioLookPickerSubTabs
                    className="studio-look-picker-subtabs studio-look-picker-subtabs--light"
                    tabs={MAKEUP_V3_LOOK_PICKER_TABS}
                    activeId={everydayLookTab}
                    onChange={setEverydayLookTab}
                    disabled={studio.busy || studio.skinBusy || studio.resolveBusy}
                    trailing={
                      <StudioOptionsFullViewTrigger
                        active={makeupOptionsFullView}
                        disabled={studio.busy}
                        onClick={toggleMakeupOptionsFullView}
                      />
                    }
                  />
                  <MakeupV3EverydayPresetsRail
                    selectedPresetId={
                      studio.presetId === MAKEUP_V3_BRIDAL_LOOK_PRESET_ID ? '' : studio.presetId
                    }
                    disabled={studio.busy || studio.skinBusy || studio.resolveBusy}
                    onSelectPreset={onPickEverydayPreset}
                    presets={everydayPresetsFiltered}
                  />
                  <p className="looks-studio__hint looks-studio__hint--tight">
                    Opens Customize after you pick a preset.
                  </p>
                </>
              ) : null}

              {menuTab === 'bridal' ? (
                <>
                  <StudioLookPickerSubTabs
                    className="studio-look-picker-subtabs studio-look-picker-subtabs--light"
                    tabs={MAKEUP_V3_LOOK_PICKER_TABS}
                    activeId={bridalLookTab}
                    onChange={setBridalLookTab}
                    disabled={studio.busy || studio.skinBusy || studio.resolveBusy}
                    trailing={
                      <StudioOptionsFullViewTrigger
                        active={makeupOptionsFullView}
                        disabled={studio.busy}
                        onClick={toggleMakeupOptionsFullView}
                      />
                    }
                  />
                  <MakeupV3BridalLooksGrid
                    selectedLookId={studio.bridalLookId}
                    disabled={studio.busy || studio.skinBusy || studio.resolveBusy}
                    onSelectLook={onPickBridalLook}
                    looks={bridalLooksFiltered}
                  />
                </>
              ) : null}

              {menuTab === 'custom' ? (
                <>
                  {studio.bridalLookId ? (
                    <p className="looks-studio__hint">
                      Bridal: {bridalLookById(studio.bridalLookId)?.label || studio.activePreset?.label}
                      — adjust face makeup below. Dress, hair, and jewellery follow the look.
                    </p>
                  ) : studio.activePreset ? (
                    <p className="looks-studio__hint">
                      Preset: {studio.activePreset.label} — tweak colors below.
                    </p>
                  ) : (
                    <p className="looks-studio__hint">
                      Pick Everyday or Bridal first, or set custom colors below.
                    </p>
                  )}
                  <MakeupV3ColorEditor
                    categories={customCategories}
                    makeupLab={studio.makeupLab}
                    onCategory={studio.onCategory}
                    onIntensity={studio.onIntensity}
                    onFinish={studio.onFinish}
                    busy={studio.busy}
                  />
                  {studio.advisory.autoFoundationRecommended ? (
                    <label className="makeup-v3-kiosk-check">
                      <input
                        type="checkbox"
                        checked={studio.applyAutoFoundation}
                        disabled={studio.busy}
                        onChange={(e) => studio.setApplyAutoFoundation(e.target.checked)}
                      />
                      Sheer auto foundation when blush/eyes need blending
                    </label>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <StudioOptionsFullViewModal
        open={makeupOptionsFullView && (menuTab === 'everyday' || menuTab === 'bridal')}
        title={makeupFullViewTitle}
        onClose={() => setMakeupOptionsFullView(false)}
      >
        {menuTab === 'everyday' ? (
          <MakeupV3EverydayPresetsRail
            selectedPresetId={
              studio.presetId === MAKEUP_V3_BRIDAL_LOOK_PRESET_ID ? '' : studio.presetId
            }
            disabled={studio.busy || studio.skinBusy || studio.resolveBusy}
            onSelectPreset={onPickEverydayPreset}
            presets={everydayPresetsFiltered}
            className="makeup-v3-kiosk-presets looks-preset-rail looks-preset-rail--kiosk looks-preset-rail--fullview"
          />
        ) : null}
        {menuTab === 'bridal' ? (
          <MakeupV3BridalLooksGrid
            selectedLookId={studio.bridalLookId}
            disabled={studio.busy || studio.skinBusy || studio.resolveBusy}
            onSelectLook={onPickBridalLook}
            looks={bridalLooksFiltered}
            showHeading={false}
          />
        ) : null}
      </StudioOptionsFullViewModal>

      <StudioPhotoSourceModal
        open={showPhotoSourceModal}
        onClose={() => setShowPhotoSourceModal(false)}
        onImageReady={(img) => {
          setShowPhotoSourceModal(false);
          applyIncomingFaceImage(img);
        }}
      />

      {qrModal}
      {resetModal}
    </div>
  );
}
